/**
 * TIPO DE SUPLEMENTO — el subtipo de una póliza Suplementaria.
 *
 * Lissette, 2026-09-08: *"en la tarjeta de pólizas suplementarias, un
 * desplegable que diga tipo de suplemento, que se pueda mapear en monday y
 * en pólizas, y que se llene con el texto que venga"*.
 *
 * POR QUE HACE FALTA. "Suplementario" es UN ramo del catálogo, pero debajo
 * caben cosas que no se parecen en nada: accidente, cáncer, indemnización
 * hospitalaria, incapacidad, dental o visión vendidos como añadido... Hasta
 * ahora todas esas pólizas se veían iguales en la tarjeta y en los informes,
 * y para saber cuál era había que abrir el plan y leerlo.
 *
 * POR QUE UN DESPLEGABLE QUE ADEMAS ACEPTA ESCRIBIR. Un desplegable cerrado
 * obligaría a que la lista estuviera completa el primer día, y no lo está:
 * cada aseguradora nombra sus productos a su manera. Y sobre todo, lo que
 * viene de un IMPORTE (monday, un Excel) es el texto que puso otra persona:
 * si no está en la lista, la opción no es "descartarlo" — es guardarlo tal
 * cual. Por eso:
 *
 *   1. La lista de abajo son SUGERENCIAS, no un enum.
 *   2. Se le suman los valores que la agencia YA usó en sus pólizas, así que
 *      el catálogo crece solo y sin que nadie lo configure.
 *   3. Cualquier texto se acepta y se guarda como llegó.
 *
 * NO HACE FALTA MIGRACION: viaja en `type_details` (POLICY_DETAIL_KEYS de
 * api.js), el mismo sitio donde ya viven el deducible del short term, el
 * máximo anual del dental o los datos del auto.
 */

/** Los que se ofrecen de fábrica. Sugerencias, no una lista cerrada. */
export const TIPOS_DE_SUPLEMENTO = [
  'Accidente',
  'Cáncer',
  'Enfermedad crítica',
  'Hospitalización / Indemnización',
  'Incapacidad (Disability)',
  'Dental',
  'Visión',
  'Vida',
  'Gastos finales',
  'Medicamentos recetados',
];

// Lo que escribe otra persona en su hoja para decir lo mismo. Solo variantes
// EVIDENTES: si no se reconoce, el texto se respeta tal cual — inventarle una
// categoría a una póliza es peor que dejarla con el nombre que le puso quien
// la vendió.
const EQUIVALENCIAS = [
  [/\baccident|accidente/i, 'Accidente'],
  [/\bcancer|cáncer|oncolog/i, 'Cáncer'],
  [/critical\s*illness|enfermedad\s*(critica|crítica)|\bcritica\b/i, 'Enfermedad crítica'],
  [/hospital|indemnizaci|indemnity/i, 'Hospitalización / Indemnización'],
  [/disabilit|incapacidad|invalidez/i, 'Incapacidad (Disability)'],
  [/\bdental\b/i, 'Dental'],
  [/\bvision\b|visión|\bvista\b/i, 'Visión'],
  [/final\s*expense|gastos\s*finales|funerari/i, 'Gastos finales'],
  [/\blife\b|seguro\s*de\s*vida|\bvida\b/i, 'Vida'],
  [/prescription|medicament|farmac|\brx\b/i, 'Medicamentos recetados'],
];

/**
 * Limpia lo que llega y, si es una forma reconocible de uno de los de
 * arriba, lo deja con ESE nombre — para que "CANCER", "Cancer Plan" y
 * "cáncer" no acaben siendo tres categorías distintas en el informe.
 *
 * Lo que no reconoce se devuelve tal cual (solo recortado): un texto que no
 * está en la lista NO es un error, es un producto que todavía no habíamos
 * visto.
 */
export function normalizarTipoDeSuplemento(v) {
  const s = String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
  if (!s) return '';
  for (const [re, nombre] of EQUIVALENCIAS) {
    if (re.test(s)) return nombre;
  }
  return s;
}

/**
 * Lo que se ofrece en el desplegable: los de fábrica, MAS los que la agencia
 * ya usó, MAS el que tenga puesto esta póliza — sin repetidos.
 *
 * El valor actual entra aunque no esté en ninguna de las dos listas: si no,
 * abrir una póliza traída de un importe enseñaría el desplegable vacío y
 * guardar sin tocar nada le borraría el dato.
 */
export function opcionesDeSuplemento(usados = [], actual = '') {
  const limpios = (Array.isArray(usados) ? usados : [])
    .map((x) => String(x == null ? '' : x).trim())
    .filter(Boolean);
  const todos = [...TIPOS_DE_SUPLEMENTO, ...limpios];
  const ahora = String(actual || '').trim();
  if (ahora) todos.push(ahora);
  const vistos = new Set();
  const salida = [];
  for (const t of todos) {
    const llave = t.toLowerCase();
    if (vistos.has(llave)) continue;
    vistos.add(llave);
    salida.push(t);
  }
  return salida;
}

/** Los tipos de suplemento que la agencia YA tiene escritos en sus pólizas. */
export function suplementosUsados(polizas = []) {
  const out = [];
  for (const p of Array.isArray(polizas) ? polizas : []) {
    const v = p && p.supplementType ? String(p.supplementType).trim() : '';
    if (v) out.push(v);
  }
  return [...new Set(out)].sort();
}
